import { useEffect, useState, useCallback } from "react";
import API from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { ListChecks, Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";

const CATEGORIES = ["general", "plumbing", "electrical", "carpentry", "painting", "roofing", "landscaping", "cleaning", "hvac", "fire_safety", "security", "mould_eradication", "other"];

const BLANK = { name: "", category: "general", auto_attach: true, active: true, items: [{ text: "", required: false }] };

export default function ChecklistTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/checklist-templates");
      setTemplates(data.templates || []);
    } catch { toast.error("Failed to load checklist templates"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...BLANK, items: [{ text: "", required: false }] }); setDialog({ open: true, editing: null }); };
  const openEdit = (t) => {
    setForm({ name: t.name, category: t.category || "general", auto_attach: t.auto_attach !== false, active: t.active !== false, items: (t.items || []).map(i => ({ ...i })) });
    setDialog({ open: true, editing: t.id });
  };

  const setItem = (idx, patch) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { text: "", required: false }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const save = async (e) => {
    e.preventDefault();
    const items = form.items.filter(i => i.text.trim());
    if (!items.length) { toast.error("Add at least one checklist item"); return; }
    setSaving(true);
    try {
      const payload = { ...form, items };
      if (dialog.editing) {
        await API.put(`/checklist-templates/${dialog.editing}`, payload);
        toast.success("Checklist updated");
      } else {
        await API.post("/checklist-templates", payload);
        toast.success("Checklist created");
      }
      setDialog({ open: false, editing: null });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete checklist "${t.name}"? Jobs that already have it attached keep their copy.`)) return;
    try {
      await API.delete(`/checklist-templates/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="checklist-templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight flex items-center gap-2">
            <ListChecks size={26} className="text-primary" /> Job Checklists
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Reusable checklists per job category. With auto-attach on, new jobs in that category get the checklist automatically.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="create-checklist-btn"><Plus size={16} className="mr-1" /> New Checklist</Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground p-8 text-center">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center" data-testid="checklists-empty">
          <ListChecks size={32} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No checklists yet. Create one — e.g. "Gas Safety Visit" with steps your operatives must tick off.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {templates.map(t => (
            <div key={t.id} className="border border-border rounded-lg p-4 bg-card" data-testid={`checklist-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{t.name}</p>
                  <p className="text-[10px] text-muted-foreground capitalize mt-0.5">
                    {(t.category || "general").replace("_", " ")} · {t.items?.length || 0} items
                    {t.auto_attach !== false && <span className="text-emerald-600"> · auto-attach</span>}
                    {t.active === false && <span className="text-red-500"> · inactive</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(t)} className="p-1.5 rounded hover:bg-muted text-muted-foreground" title="Edit" data-testid={`edit-checklist-${t.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => remove(t)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-red-500" title="Delete" data-testid={`delete-checklist-${t.id}`}><Trash size={14} /></button>
                </div>
              </div>
              <ul className="mt-2 space-y-1">
                {(t.items || []).slice(0, 4).map(i => (
                  <li key={i.id} className="text-xs text-muted-foreground truncate">• {i.text}{i.required && <span className="text-red-500"> *</span>}</li>
                ))}
                {(t.items || []).length > 4 && <li className="text-[10px] text-muted-foreground">+ {(t.items || []).length - 4} more…</li>}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog(d => ({ ...d, open: o }))}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="checklist-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.editing ? "Edit Checklist" : "New Checklist"}</DialogTitle>
            <DialogDescription>Items marked required show a warning if a job is completed without them ticked.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required placeholder="e.g. Boiler Service Checklist" className="mt-1" data-testid="checklist-name-input" />
            </div>
            <div>
              <Label>Job Category</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="mt-1" data-testid="checklist-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c.replace("_", " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Items</Label>
              <div className="space-y-2 mt-1">
                {form.items.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input value={it.text} onChange={e => setItem(idx, { text: e.target.value })} placeholder={`Step ${idx + 1}`} className="flex-1 h-8 text-xs" data-testid={`checklist-item-input-${idx}`} />
                    <label className="flex items-center gap-1 text-[10px] text-muted-foreground shrink-0 cursor-pointer" title="Required before completion">
                      <input type="checkbox" checked={it.required} onChange={e => setItem(idx, { required: e.target.checked })} className="rounded" data-testid={`checklist-item-required-${idx}`} /> req
                    </label>
                    <button type="button" onClick={() => removeItem(idx)} className="text-muted-foreground hover:text-red-500 shrink-0" data-testid={`checklist-item-remove-${idx}`}><X size={13} /></button>
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" size="sm" className="mt-2 h-7 text-[11px]" onClick={addItem} data-testid="checklist-add-item-btn"><Plus size={12} className="mr-1" /> Add item</Button>
            </div>
            <div className="flex items-center justify-between border border-border rounded-md p-3">
              <div>
                <p className="text-xs font-medium">Auto-attach to new jobs</p>
                <p className="text-[10px] text-muted-foreground">Applies to jobs created in this category</p>
              </div>
              <Switch checked={form.auto_attach} onCheckedChange={v => setForm(f => ({ ...f, auto_attach: v }))} data-testid="checklist-auto-attach-toggle" />
            </div>
            {dialog.editing && (
              <div className="flex items-center justify-between border border-border rounded-md p-3">
                <p className="text-xs font-medium">Active</p>
                <Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} data-testid="checklist-active-toggle" />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog({ open: false, editing: null })}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="checklist-save-btn">{saving ? "Saving…" : "Save Checklist"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
